/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logNextWizardStep, logProjectCreated } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { BasicInfoScreen } from 'src/customText/verticals/projectCreationModal/components/BasicInfoScreen';
import { ChooseContainerScreen } from 'src/customText/verticals/projectCreationModal/components/ChooseContainerScreen';
import { ClassificationProjectTypeScreen } from 'src/customText/verticals/projectCreationModal/components/ClassificationProjectTypeScreen';
import { ConnectStorageScreen } from 'src/customText/verticals/projectCreationModal/components/ConnectStorageScreen';
import { ReviewAndFinishScreen } from 'src/customText/verticals/projectCreationModal/components/ReviewAndFinishScreen';
import { StepsNavigator } from 'src/customText/verticals/projectCreationModal/components/StepsNavigator';
import styled from 'styled-components';

export type ProjectCreationModalBase = BasicModalProps<object> & { isExtractionProject?: boolean };
export type ProjectInfoDto = ProjectStorageInfo & ProjectBasicInfo & { projectKind: CustomTextProjectKind };
export type ProjectBasicInfo = {
    projectName: string;
    projectDescription: string;
    defaultLanguage: string;
    multiLingual: boolean;
};
export type ProjectStorageInfo = { storageAccountName: string; containerName: string; labelsLocation: string; isNewFile: boolean };

/**
 * Determines the project creation steps.
 */
export enum ProjectCreationSteps {
    ConnectStorage = 0,
    ProjectType = 1,
    EnterBasicInformation = 2,
    ChooseContainer = 3,
    ReviewAndFinish = 4
}

const messages = defineMessages({
    modalTitle: {
        id: 'customText.projectCreationModal.modalTitle',
        defaultMessage: 'Create a project'
    }
});

export const projectCreationContentLeftMargin = '32px';

const { styleComponent } = getStylistV2('ProjectCreationModalBase');
const modalTheme = getScopedTheme('ProjectCreationModalBase');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { paddingTop: '0px', paddingBottom: '0px', borderTop: borderStyle }
});
const ContentWrapper = styleComponent<IStackProps>(Stack)('ContentWrapper', () => ({
    paddingTop: '28px',
    width: '100%',
    minHeight: '500px',
    color: modalTheme.defaultTextColor,
    minWidth: 0,
    position: 'relative'
}));
const NavigatorWrapper = styled(Stack.Item)<{ isLargeOrGreater: boolean }>(props => ({
    width: props.isLargeOrGreater ? '300px' : '',
    borderRight: props.isLargeOrGreater ? borderStyle : '',
    padding: `${props.isLargeOrGreater ? '34px 0 0 10px' : '12px 20px'}`
}));

const getInitialProjectDto = (projectKind: CustomTextProjectKind) => ({
    projectKind,
    projectName: '',
    isNewFile: true,
    containerName: '',
    labelsLocation: '',
    defaultLanguage: '',
    multiLingual: false,
    projectDescription: '',
    storageAccountName: ''
});

/**
 * Represents the customText project creation modal.
 */
export const ProjectCreationModal = withModalMounter((props: ProjectCreationModalBase) => {
    const { isExtractionProject, onSubmit, onDismiss } = props;
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { isLargeOrGreater } = useResponsiveMode();

    const [projectInfoDto, updateProjectInfoDto] = useUpdateState<ProjectInfoDto>(
        getInitialProjectDto(isExtractionProject ? 'CustomEntityRecognition' : null)
    );
    const [activeStep, setActiveStep] = React.useState(ProjectCreationSteps.ConnectStorage);

    const handleStepSubmissions = (dto: Partial<ProjectInfoDto>) => {
        if (activeStep === ProjectCreationSteps.ReviewAndFinish) {
            onSubmit(projectInfoDto);
            logProjectCreated(serviceName, dto.projectName);
            logNextWizardStep(serviceName, 'CreateProject', 'ReviewAndFinish', '');
            return;
        }

        updateProjectInfoDto(dto);

        if (isExtractionProject && activeStep === ProjectCreationSteps.ConnectStorage) {
            setActiveStep(ProjectCreationSteps.EnterBasicInformation);
            logNextWizardStep(serviceName, 'CreateProject', 'ConnectStorage', '');
        } else {
            setActiveStep(activeStep + 1);
            logNextWizardStep(serviceName, 'CreateProject', ProjectCreationSteps[activeStep], '');
        }
    };

    const onDismissStep = (stepName: number) => {
        onDismiss();
        logNextWizardStep(serviceName, 'CreateProject', ProjectCreationSteps[stepName], 'Cancel');
    };

    const getStepContent = () => {
        switch (activeStep) {
            case ProjectCreationSteps.ConnectStorage: {
                return (
                    <ConnectStorageScreen
                        onSubmit={() => handleStepSubmissions({})}
                        onDismiss={() => onDismissStep(activeStep)}
                        isExtractionProjects={isExtractionProject}
                    />
                );
            }
            case ProjectCreationSteps.ProjectType:
                return (
                    <ClassificationProjectTypeScreen
                        onDismiss={() => onDismissStep(activeStep)}
                        projectDto={projectInfoDto}
                        onPrevious={() => setActiveStep(ProjectCreationSteps.ConnectStorage)}
                        onSubmit={projectKind => handleStepSubmissions({ projectKind })}
                    />
                );
            case ProjectCreationSteps.EnterBasicInformation:
                return (
                    <BasicInfoScreen
                        onDismiss={() => onDismissStep(activeStep)}
                        projectDto={projectInfoDto}
                        onPrevious={() =>
                            setActiveStep(isExtractionProject ? ProjectCreationSteps.ConnectStorage : ProjectCreationSteps.ProjectType)
                        }
                        isExtractionProject={isExtractionProject}
                        onSubmit={basicInfo => handleStepSubmissions({ ...basicInfo })}
                    />
                );
            case ProjectCreationSteps.ChooseContainer:
                return (
                    <ChooseContainerScreen
                        onDismiss={() => onDismissStep(activeStep)}
                        projectDto={projectInfoDto}
                        isExtractionProject={isExtractionProject}
                        onPrevious={() => setActiveStep(ProjectCreationSteps.EnterBasicInformation)}
                        onSubmit={(storageAccountName, containerName, labelsLocation, isNewFile) =>
                            handleStepSubmissions({ storageAccountName, containerName, labelsLocation, isNewFile })
                        }
                    />
                );
            case ProjectCreationSteps.ReviewAndFinish:
                return (
                    <ReviewAndFinishScreen
                        onDismiss={() => onDismissStep(activeStep)}
                        projectDto={projectInfoDto}
                        onSubmit={() => handleStepSubmissions({})}
                        onPrevious={() => setActiveStep(ProjectCreationSteps.ChooseContainer)}
                    />
                );
            default:
        }
    };

    return (
        <BasicModal
            {...props}
            minWidth={976}
            shouldRenderFooter={false}
            dialogContentProps={{ title: localize(messages.modalTitle), styles: dialogContentStyles }}>
            <Stack horizontal={isLargeOrGreater} verticalFill>
                <NavigatorWrapper isLargeOrGreater={isLargeOrGreater}>
                    <StepsNavigator
                        activeStep={activeStep}
                        horizontal={!isLargeOrGreater}
                        onActiveStepChange={setActiveStep}
                        isExtractionProject={isExtractionProject}
                    />
                </NavigatorWrapper>

                <ContentWrapper grow verticalFill>
                    {getStepContent()}
                </ContentWrapper>
            </Stack>
        </BasicModal>
    );
});
